import servicesService from '@/modules/services/service/servicesService';

const state = {
  services: [],
  loading: false,
  completed: false,
};

const mutations = {
  addServices(state, services) {
    state.services = state.services.concat(services);
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setCompleted(state, completed) {
    state.completed = completed;
  },
  resetServices(state) {
    state.services = [];
    state.completed = false;
  },
};

// Shared by concurrent callers so a second request is not fired and, more importantly, so an
// awaiting caller waits for the real fetch instead of returning early: AiChatPanel dispatches
// loadAllServices un-awaited while the Resources pages await it. Deliberately module-level
// rather than state: it is a request guard, not data, and it must survive resetServices -
// which clears the list but must not orphan an outstanding request.
let inFlight = null;

const actions = {
  async loadServices({ state, dispatch }) {
    if (state.services.length) return;
    await dispatch('loadAllServices');
  },
  // settings/services returns the business's whole list in one response, so a single fetch
  // always completes the list - there are no pages to walk (VCITA2-15147).
  async loadAllServices({ state, commit }) {
    if (state.completed) return;
    if (inFlight) {
      await inFlight;
      return;
    }
    commit('setLoading', true);

    inFlight = (async () => {
      try {
        const services = await servicesService.fetchServices();
        // Swap the list only once the fetch succeeded: resetting first would leave the store
        // empty on a network error, showing a blank list where services had been.
        if (state.services.length > 0) {
          commit('resetServices');
        }
        commit('addServices', services);
        commit('setCompleted', true);
      } catch (e) {
        console.log('Error loading services', e); // eslint-disable-line no-console
      } finally {
        commit('setLoading', false);
        inFlight = null;
      }
    })();

    await inFlight;
  },
  // Kept for the infinite-scroll callers: the first load already returns everything.
  async loadMoreServices({ dispatch }) {
    await dispatch('loadAllServices');
  },
};

const getters = {
  services: (state) => state.services,
  getServiceByUid: (state) => (uid) => state.services.find((service) => service.id === uid),
  loading: (state) => state.loading,
  completed: (state) => state.completed,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
